import { CartUpdateEvent } from '@theme/events';

/**
 * Mystery Pair upsell (sections/upsell-modal.liquid).
 *
 * Same flow as the original popup: every click on the cart's Checkout button
 * opens the offer first (as a modal <dialog>, above the cart drawer). Whatever
 * the shopper does next carries on to checkout: "No thanks", the X, Escape or
 * the backdrop straight away, and adding after a short "Added" confirmation.
 */

const ADDED_PAUSE = 900;
const MAX_QTY = 10;

/** @param {number} cents */
const money = (cents) => {
  const value = cents / 100;
  return '$' + (cents % 100 ? value.toFixed(2) : String(value));
};

class MysteryUpsell extends HTMLElement {
  /** @type {{id: number, height: string, size: string, price: number, compare: number, available: boolean}[]} */
  variants = [];
  heights = [];
  sizes = [];
  state = { height: '', size: '', qty: 1, added: false, cartTotal: 0 };
  /** @type {HTMLButtonElement | null} */
  checkoutButton = null;
  bypass = false;
  lastFocus = null;

  connectedCallback() {
    const data = this.querySelector('[data-owmp-variants]');
    this.variants = data ? JSON.parse(data.textContent || '[]') : [];
    this.heights = [...new Set(this.variants.map((v) => v.height))];
    this.sizes = [...new Set(this.variants.map((v) => v.size))];
    this.threshold = Number(this.dataset.shippingThreshold) || 0;
    this.productId = Number(this.dataset.productId);

    // Move to <body> so no ancestor's stacking/transform can clip the overlay.
    if (this.parentElement !== document.body) {
      document.body.appendChild(this);
      return;
    }

    this.dialog = this.$('backdrop');
    document.addEventListener('click', this.onDocumentClick, true);
    window.addEventListener('pageshow', this.onPageShow);
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    // Escape fires "cancel" on a modal dialog: treat it like the X.
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.continueToCheckout();
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick, true);
    window.removeEventListener('pageshow', this.onPageShow);
  }

  /** Coming back from checkout with the browser's Back button: start closed. */
  onPageShow = (event) => {
    if (event.persisted) this.hide();
  };

  $(name) {
    return this.querySelector(`[data-owmp="${name}"]`);
  }

  /** Checkout clicks anywhere (cart drawer or cart page). */
  onDocumentClick = (event) => {
    // Only the real checkout button; never the "Choose Free Gift" prompt.
    const button = event.target instanceof Element ? event.target.closest('button#checkout:not([data-cart-gift-choose])') : null;
    if (!button || this.bypass || !this.variants.length) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.checkoutButton = button;
    this.open();
  };

  async open() {
    this.lastFocus = document.activeElement;
    let cart = null;
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } });
      cart = await response.json();
    } catch (error) {
      console.warn('[Mystery upsell] could not read the cart', error);
    }

    const items = cart?.items || [];
    this.state.added = false;
    this.state.qty = 1;
    this.setFitOpen(false);
    this.state.cartTotal = cart?.total_price || 0;
    this.preselect(items);
    this.render();

    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      this.querySelector('.owmp-dialog')?.focus({ preventScroll: true });
    });
  }

  hide() {
    clearTimeout(this.addedTimer);
    this.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    if (this.dialog.open) this.dialog.close();
  }

  continueToCheckout() {
    this.hide();
    const button = this.checkoutButton?.isConnected ? this.checkoutButton : document.querySelector('button#checkout');
    if (button instanceof HTMLButtonElement) {
      this.bypass = true;
      button.click();
      this.bypass = false;
    } else {
      window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
    }
  }

  /** Pre-select the height and size the shopper already has the most of. */
  preselect(items) {
    const heightCount = {};
    const sizeCount = {};
    for (const item of items) {
      const title = `${item.product_title || ''} ${item.product_type || ''}`.toLowerCase();
      for (const height of this.heights) {
        const word = height.toLowerCase();
        const matches = title.includes(word) || (word === 'ankle' && title.includes('no show'));
        if (matches) heightCount[height] = (heightCount[height] || 0) + item.quantity;
      }
      const variantTitle = (item.variant_title || '').toUpperCase();
      for (const size of this.sizes) {
        if (variantTitle.includes(size.toUpperCase())) sizeCount[size] = (sizeCount[size] || 0) + item.quantity;
      }
    }
    const top = (counts, fallback) => {
      const keys = Object.keys(counts);
      return keys.length ? keys.sort((a, b) => counts[b] - counts[a])[0] : fallback;
    };
    this.state.height = top(heightCount, this.heights.includes('Crew') ? 'Crew' : this.heights[0]);
    this.state.size = top(sizeCount, this.sizes.includes('M/L') ? 'M/L' : this.sizes[0]);
    this.fixSoldOut();
  }

  variantFor(height, size) {
    return this.variants.find((v) => v.height === height && v.size === size);
  }

  fixSoldOut() {
    if (this.variantFor(this.state.height, this.state.size)?.available) return;
    const free = this.sizes.find((size) => this.variantFor(this.state.height, size)?.available);
    if (free) this.state.size = free;
  }

  render() {
    const { height, size, added, cartTotal } = this.state;
    const variant = this.variantFor(height, size) || this.variants[0];

    this.$('heights').innerHTML = this.heights
      .map(
        (h) =>
          `<button type="button" class="owmp-opt" data-h="${h}" aria-pressed="${h === height}">${h.toUpperCase()}</button>`
      )
      .join('');
    this.$('sizes').innerHTML = this.sizes
      .map((s) => {
        const soldOut = !this.variantFor(height, s)?.available;
        return `<button type="button" class="owmp-opt" data-s="${s}" aria-pressed="${s === size}"${
          soldOut ? ` disabled aria-label="${s}, sold out"` : ''
        }>${s}</button>`;
      })
      .join('');

    // Price and regular price are for the chosen quantity, not one pair.
    const { qty } = this.state;
    const saving = variant.compare - variant.price;
    const percent = variant.compare ? Math.round((saving / variant.compare) * 100) : 0;
    this.$('price').textContent = money(variant.price * qty);
    this.$('compare').textContent = money(variant.compare * qty);
    this.$('off').textContent = `${percent}% OFF`;
    const sticker = this.$('sticker');
    if (sticker) {
      sticker.hidden = percent <= 0;
      this.$('stickerpct').textContent = `${percent}%`;
    }

    this.$('fitvalue').textContent = `${variant.height} · ${variant.size}`;
    this.$('qty').textContent = String(qty);
    this.$('minus').disabled = added || qty <= 1;
    this.$('plus').disabled = added || qty >= MAX_QTY;

    const unlocksShipping = this.renderShipping(variant.price);

    const add = this.$('add');
    add.classList.toggle('is-added', added);
    add.innerHTML = added
      ? '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>ADDED TO YOUR ORDER'
      : unlocksShipping
        ? `ADD ${qty > 1 ? `${qty} ` : ''}+ FREE SHIPPING · ${money(variant.price * qty)}`
        : `ADD ${qty > 1 ? `${qty} ` : ''}TO ORDER · ${money(variant.price * qty)}`;
  }

  /**
   * Free-shipping row under the price.
   *  - "count": how many pairs of the selected sock reach free shipping, counted
   *    down by the quantity ("5 pairs of these…" -> "3 more pairs unlock…" ->
   *    "1 more pair unlocks…" -> "You've unlocked free shipping"). The bar is one fill toward this
   *    country's threshold: the cart total plus the selection.
   *  - "unlocked": the cart already has free shipping; the row says so.
   *  - "off": free shipping isn't set up for this country; no row.
   * @param {number} unitPrice - selected variant price, in cents
   * @returns {boolean} whether this selection takes the cart to free shipping
   *   (the button then reads "ADD … + FREE SHIPPING")
   */
  renderShipping(unitPrice) {
    const ship = this.$('ship');
    if (!ship) return false;
    const { qty, cartTotal } = this.state;
    const gap = this.threshold - cartTotal;
    ship.dataset.unlocking = 'false';

    if (!(this.threshold > 0) || !(unitPrice > 0)) {
      ship.dataset.mode = 'off';
      return false;
    }
    if (gap <= 0) {
      ship.dataset.mode = 'unlocked';
      return false;
    }

    ship.dataset.mode = 'count';
    const needed = Math.ceil(gap / unitPrice);
    const more = needed - qty;
    // At 1 pair: the total it takes ("5 pairs of these…"). Each pair added
    // after that counts down what's left ("3 more pairs…", "1 more pair…").
    let message;
    if (more <= 0) message = "You've unlocked free shipping";
    else if (qty > 1) message = `${more} more ${more === 1 ? 'pair unlocks' : 'pairs unlock'} free shipping`;
    else message = needed === 1 ? '1 pair of these gets you free shipping' : `${needed} pairs of these get you free shipping`;
    this.$('shipmsg').textContent = message;
    ship.dataset.unlocking = String(more <= 0);

    const withSelection = cartTotal + unitPrice * qty;
    this.$('fill').style.width = `${Math.min(100, (withSelection / this.threshold) * 100)}%`;
    return more <= 0;
  }

  /** Show or hide the Height/Size pills under "Your fit"; the button reads CHANGE / DONE. */
  setFitOpen(open) {
    const fit = this.$('fit');
    if (!fit) return;
    fit.dataset.open = String(open);
    const button = this.$('change');
    if (button) {
      button.setAttribute('aria-expanded', String(open));
      button.textContent = open ? 'Done' : 'Change';
    }
  }

  async addToCart() {
    if (this.state.added) return;
    const variant = this.variantFor(this.state.height, this.state.size);
    if (!variant?.available) return;

    const add = this.$('add');
    add.setAttribute('aria-busy', 'true');
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: this.state.qty }], sections: sectionIds.join(',') }),
      });
      if (!response.ok) throw new Error(`add failed ${response.status}`);
      const result = await response.json();

      this.state.added = true;
      this.render();
      this.$('live').textContent = 'Mystery Pair added to your order.';

      this.dispatchEvent(
        new CartUpdateEvent({}, 'mystery-upsell', {
          source: 'mystery-upsell',
          variantId: String(variant.id),
          sections: result.sections || undefined,
        })
      );

      this.addedTimer = setTimeout(() => this.continueToCheckout(), ADDED_PAUSE);
    } catch (error) {
      console.error('[Mystery upsell]', error);
      this.$('live').textContent = "That didn't add. Try again.";
    } finally {
      add.removeAttribute('aria-busy');
    }
  }

  onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    if (target === this.dialog) return this.continueToCheckout();

    const button = target.closest('button');
    if (!button || button.disabled) return;

    if (button.dataset.h) {
      this.state.height = button.dataset.h;
      this.fixSoldOut();
      this.render();
    } else if (button.dataset.s) {
      this.state.size = button.dataset.s;
      this.render();
    } else if (button.dataset.owmp === 'change') {
      this.setFitOpen(this.$('fit')?.dataset.open !== 'true');
    } else if (button.dataset.owmp === 'minus' || button.dataset.owmp === 'plus') {
      const step = button.dataset.owmp === 'plus' ? 1 : -1;
      this.state.qty = Math.min(MAX_QTY, Math.max(1, this.state.qty + step));
      this.render();
    } else if (button.dataset.owmp === 'add') {
      this.addToCart();
    } else if (button.dataset.owmp === 'decline' || button.dataset.owmp === 'close') {
      this.continueToCheckout();
    }
  };

  onKeydown = (event) => {
    if (event.key !== 'Tab') return;
    const focusable = [...this.querySelectorAll('button:not([disabled])')].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };
}

if (!customElements.get('mystery-upsell')) {
  customElements.define('mystery-upsell', MysteryUpsell);
}
